/*
 * Copyright (c) ProviderTrust LLC.
 * All Rights Reserved.
 *
 * This software is confidential and proprietary information of
 * ProviderTrust LLC ("Confidential Information"). You shall not disclose
 * such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered
 * into with ProviderTrust.
 */

export const requiredRule = value => !!value.trim() || "This field is required."

export const npiRule = value =>
  /^\d{10}$/.test(value) ||
  "NPI is not valid. " +
    "Valid NPI is 10 digits with no extra characters or spaces"

export const SYSTEM_ADMIN_ROLE = "system_admin_user"

export const DOMAIN_ADMIN_ROLE = "domain_admin_user"

/**
 * the `type` is 'vue' for vue page, and anything else for proteus page
 * the `roles` is only required when page is secured
 * `hide` defines a role like check based on vuex getter for hiding the menu item
 */
export const menus = [
  {
    title: "Search",
    icon: "fa-search",
    menuItems: [
      {
        title: "Interactive Search",
        icon: "fa-search",
        link: "/interactive/search"
      },
      {
        title: "Batch Search (New)",
        icon: "fa-search-plus",
        link: "/flex-search",
        type: "vue"
      },
      {
        title: "Batch Name Search",
        icon: "fa-files-o",
        link: "/batch/name/batchUI",
        roles: ["name_search_user"]
      },
      {
        title: "Batch NPI Search",
        icon: "fa-files-o",
        link: "/batch/npi/batchUI",
        roles: ["npi_search_user"]
      },
      {
        title: "Advance Search",
        icon: "fa-magic",
        link: "/advancedSearch/search",
        roles: ["advanced_search"]
      },
      {
        title: "Search History",
        icon: "fa-history",
        link: "/searchhistory/main",
        roles: ["paying_user"]
      }
    ]
  },
  {
    title: "Settings",
    icon: "fa-cogs",
    menuItems: [
      {
        title: "Account Details",
        icon: "fa-briefcase",
        link: "/account-details",
        roles: ["account_owner_user"],
        type: "vue"
      },
      {
        title: "Client Admin",
        icon: "fa-building",
        link: "/admin",
        roles: [SYSTEM_ADMIN_ROLE]
      },
      {
        title: "Teams",
        icon: "fa-users",
        link: "/admin/domainHierarchy",
        roles: [DOMAIN_ADMIN_ROLE],
        hide: "isSingleTeam"
      },
      {
        title: "Team Settings",
        icon: "fa-users",
        link: "/admin/domainHierarchy",
        roles: [DOMAIN_ADMIN_ROLE],
        hide: "isMultiTeam"
      },
      {
        title: "Users",
        icon: "fa-user",
        link: "/admin/userManagement",
        roles: [DOMAIN_ADMIN_ROLE]
      },
      {
        title: "Batch Queue Management",
        icon: "fa-list-ul",
        link: "/admin/queueManagement",
        roles: [SYSTEM_ADMIN_ROLE]
      },
      {
        title: "API Key Management",
        icon: "fa-key",
        link: "/npi/apikeys",
        roles: ["api_user"]
      },
      {
        title: "API Documentation",
        icon: "fa-book",
        link: "/npi/apidoc",
        roles: ["api_user"]
      }
    ]
  }
]

/**
 * Line item pricing
 * @type {Array}
 */
// TODO pull from PricingItem.java
export const prices = [
  {
    id: "NAME",
    name: "Search exclusions by name",
    price: 0.15,
    unit: "search"
  },
  { id: "NPI", name: "Search exclusions by NPI", price: 0.2, unit: "search" },
  { id: "OFAC", name: "Search OFAC", price: 0.05, unit: "search" },
  { id: "SSNDMF", name: "Search SSNDMF", price: 0.05, unit: "search" },
  { id: "MULTI_TEAM", name: "Unlimited Teams", price: 75.0, unit: "month" },
  { id: "SINGLE", name: "Single Team", price: 30.0, unit: "month" }
]

export const vuetifySetting = {
  theme: {
    primary: "#4485B5",
    error: "#B20000",
    success: "#2ab342",
    info: "#fbc751"
  }
}

export const REPORT_ATTRIBUTE_NAME = "ecreportData"
export const REPORT_METADATA_FILENAME = "METADATA_FILENAME"
export const REPORT_METADATA_ANALYTICS_EVENT = "ANALYTICS_EVENT"

export const API = {
  UPLOAD_ENDPOINT: "/services/fileupload",
  GETFILE_ENDPOINT: "/services/flexsearchs/:batchId/file",
  LIST_ENDPOINT: "/services/flexsearchs",
  GET_BYID_ENDPOINT: "/services/flexsearchs/:batchId",
  SEARCHTYPE_ENDPOINT: "/services/flexsearchs/searchtypes",
  SOURCES: "/services/utils/sources",
  COLUMNMAPPING_ENDPOINT: "/services/flexsearchs/:batchId/columnmappings",
  FLEXSEARCH_KILL_ENDPOINT: "/services/flexsearchs/:batchId/kill",
  FLEXSEARCH_TRASH_ENDPOINT: "/services/flexsearchs/:batchId/trash",
  FLEXSEARCH_RESUBMIT_ENDPOINT: "/services/flexsearchs/:batchId/resubmit",
  FLEXSEARCH_FILTER_ENDPOINT: "/services/flexsearchs/filters",
  FLEXBATCH_RESULT_TYPES: "/services/flexsearchs/:batchId/result",
  FLEXBATCH_NPI_DETAILS: "/services/flexsearchs/:batchId/npidetails",
  FLEXBATCH_NPI_DETAILS_BYIDS: "/services/flexsearchs/:batchId/npidetails/:ids",
  FLEXBATCH_NPI_DETAILS_BYID: "/services/flexsearchs/:batchId/npidetails/:id",
  FLEXBATCH_SANCTION_DETAILS: "/services/flexsearchs/:batchId/namedetails",
  FLEXBATCH_SANCTION_DETAILS_BYIDS:
    "/services/flexsearchs/:batchId/namedetails/:ids",
  FLEXBATCH_SANCTION_DETAILS_BYID:
    "/services/flexsearchs/:batchId/namedetails/:id",
  FLEXBATCH_SSDMF_DETAILS: "/services/flexsearchs/:batchId/ssdmfdetails",
  FLEXBATCH_SSDMF_DETAILS_BYIDS:
    "/services/flexsearchs/:batchId/ssdmfdetails/:ids",
  FLEXBATCH_SSDMF_DETAILS_BYID:
    "/services/flexsearchs/:batchId/ssdmfdetails/:id",
  FLEXBATCH_AUDIT_REPORT_BYID: "/services/flexsearchs/:batchId/auditreport",
  FLEXBATCH_EXCLUSION_RECORDS_REPORT_BYID:
    "/services/flexsearchs/:batchId/exclusionrecordsreport"
}

export const RECORDS_PER_PAGE = 30
export const SEARCH_DEBOUNCE_TIMEOUT = 500

export const nameStatusInfos = {
  NONCLEARED: { class: "statuswarn", text: "Needs Review" },
  HISTORICAL_MATCH: { class: "statusalert", text: "Reviewed: Match" },
  CLEARED: { class: "statussuccess", text: "Reviewed: Clear" },
  UNDER_REVIEW: { class: "statusinfo", text: "Under Review" }
}

export const statusInfos = {
  NONCLEARED: { class: "statuswarn", text: "Needs Review" },
  UNDER_REVIEW: { class: "statusinfo", text: "Under Review" },
  HISTORICAL_MATCH: { class: "statussuccess", text: "Reviewed" }
}

export const redStatus = [
  "Invalid",
  "Inactive",
  "Found on Opt Out List",
  "Exclusion Found",
  "Error",
  "Found on SSDMF"
]

export const statusColor = status => {
  if (redStatus.indexOf(status) !== -1) {
    return "#C80000"
  } else {
    return "#3A3A3A"
  }
}

export const batchStatuses = ["Processing", "Submitted", "Pending"]

export const batchStatusesExt = [
  "Processing",
  "Submitted",
  "Pending",
  "Internal Error - contact support",
  "Batch Kill Requested"
]

export const createFilterList = items => {
  return Object.entries(items).map(item => ({
    value: item[0],
    text: item[1].text
  }))
}

export const optOutStatuses = {
  true: { text: "Found on Opt Out List" },
  false: { text: "Not Found on Opt Out List" }
}

export const exclusionStatuses = {
  true: { text: "Exclusion Found" },
  false: { text: "No Exclusions Found" }
}

export const npiStatuses = {
  VALID: { text: "Valid", value: ["CLEARED", "EXCLUDED"] },
  INACTIVE: { text: "Inactive", value: ["INACTIVE"] },
  INVALID: { text: "Invalid", value: ["INVALID"] },
  ERROR: { text: "Error", value: ["PENDING", "ERROR"] }
}



// WEBPACK FOOTER //
// ./src/common/constants.js